import { router } from "@inertiajs/react"
import { ExternalLink, Heart } from "lucide-react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import { ThumbnailWithFallback } from "@/components/shared/thumbnail-with-fallback"
import { Alert, AlertTitle } from "@/components/ui/alert"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import type { MissionCoupDeCoeur, Submission } from "@/types/submissions"
import { SUBMISSION_CAP } from "@/types/submissions"

import { ApproveSubmissionConfirmDialog } from "./approve-submission-confirm-dialog"
import { CoupDeCoeurConfirmDialog } from "./coup-de-coeur-confirm-dialog"
import { FlagSubmissionConfirmDialog } from "./flag-submission-confirm-dialog"
import { SubmissionStateChip } from "./submission-state-chip"

/**
 * OKS-35 — Brand-side submissions Sheet (UX Spec §4).
 *
 * Anchored on the OKS-31 collab row's "View submissions (N/3)" overflow
 * action. URL-synced via `?submissions=<collabId>` so the Sheet is
 * deep-linkable and refresh-safe (UX Spec §1).
 *
 * Renders 5 states (empty / loading / error / success / edge). Per-row
 * Approve / Flag actions render only when `state === "pending"`;
 * approved + flagged rows show audit metadata (actor + timestamp)
 * instead.
 *
 * Footer microcopy reminds the operator that first-approval marks
 * the collab Completed (PRD §3 + UX Spec §4).
 */
interface Props {
  open: boolean
  collabId: number | null
  collabCreatorHandle: string | null
  missionId: number
  submissions: Submission[]
  /**
   * OKS-399 — gates the approve-dialog kudo composer (FR-11). When
   * false, the dialog renders plain approve; the auto-checks panel still
   * shows. Defaults false (fail-closed) when the payload is absent.
   */
  canSendKudo?: boolean
  /**
   * OKS-964 — the MISSION's Coup de cœur, or null when unpicked. Mission-level
   * rather than collab-level on purpose: the cap is per mission but this Sheet
   * shows one collab, so without it a Sheet on collab B could not explain why
   * its approved rows offer nothing (UX §2.3 + §2.4, PRD OQ-4).
   */
  coupDeCoeur?: MissionCoupDeCoeur | null
  loading?: boolean
  errorCode?: string | null
  agencyDropped?: boolean
  onOpenChange: (next: boolean) => void
}

export function BrandSubmissionsSheet({
  open,
  collabId,
  collabCreatorHandle,
  missionId,
  submissions,
  canSendKudo = false,
  coupDeCoeur = null,
  loading = false,
  errorCode = null,
  agencyDropped = false,
  onOpenChange,
}: Props) {
  const { t } = useTranslation()

  // URL-sync: when the Sheet opens, push `?submissions=<collabId>`;
  // when it closes, strip it. Uses Inertia `router.replace` so we don't
  // burn a history entry per toggle.
  useEffect(() => {
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (open && collabId !== null) {
      url.searchParams.set("submissions", String(collabId))
    } else {
      url.searchParams.delete("submissions")
    }
    const next = `${url.pathname}${url.search}${url.hash}`
    if (
      next !==
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    ) {
      // Inertia 2 — `router.replace` takes options object with `url`. We
      // only need a URL swap (no XHR), so `preserveState/preserveScroll`
      // keep the rest of the page unaffected.
      router.replace({ url: next, preserveState: true, preserveScroll: true })
    }
  }, [open, collabId])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg"
        data-testid={`brand-submissions-sheet-${collabId ?? "none"}`}
      >
        <SheetHeader>
          <SheetTitle>
            {t("submissions.brand.sheet_title", {
              handle: collabCreatorHandle ?? "—",
            })}
          </SheetTitle>
          <SheetDescription>
            {t("submissions.brand.footer_microcopy")}
          </SheetDescription>
          {/* OKS-964 UX §2.4 — the mission-level cap line. This is what makes
              §2.3's silence legible: an approved row in a picked mission renders
              NOTHING, and without this line the brand cannot tell that from a
              permissions problem or a bug. Text + handle, never colour alone. */}
          {coupDeCoeur && (
            <p
              className="text-muted-foreground text-sm"
              data-testid="sheet-coup-de-coeur-line"
            >
              {t("submissions.brand.coup_de_coeur.mission_line", {
                handle: coupDeCoeur.creator_handle ?? "—",
              })}
            </p>
          )}
        </SheetHeader>

        <SheetBody
          loading={loading}
          errorCode={errorCode}
          submissions={submissions}
          missionId={missionId}
          collabCreatorHandle={collabCreatorHandle}
          canSendKudo={canSendKudo}
          coupDeCoeur={coupDeCoeur}
          agencyDropped={agencyDropped}
        />

        <SheetFooter className="text-muted-foreground text-xs">
          {submissions.length}/{SUBMISSION_CAP}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

interface SheetBodyProps {
  loading: boolean
  errorCode: string | null
  submissions: Submission[]
  missionId: number
  collabCreatorHandle: string | null
  canSendKudo: boolean
  coupDeCoeur: MissionCoupDeCoeur | null
  agencyDropped: boolean
}

function SheetBody({
  loading,
  errorCode,
  submissions,
  missionId,
  collabCreatorHandle,
  canSendKudo,
  coupDeCoeur,
  agencyDropped,
}: SheetBodyProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div
        className="flex flex-col gap-3 py-4"
        data-testid="brand-submissions-sheet-loading"
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (errorCode) {
    return (
      <Alert
        variant="destructive"
        className="my-4"
        data-testid="brand-submissions-sheet-error"
      >
        <AlertTitle>{t("submissions.errors.default")}</AlertTitle>
      </Alert>
    )
  }

  if (submissions.length === 0) {
    return (
      <div
        className="flex flex-col gap-1 py-6"
        data-testid="brand-submissions-sheet-empty"
      >
        <p className="text-sm font-medium">
          {t("submissions.brand.empty_title")}
        </p>
        <p className="text-muted-foreground text-sm">
          {t("submissions.brand.empty_helper")}
        </p>
      </div>
    )
  }

  return (
    <ul
      className="flex flex-col gap-3 py-4"
      data-testid="brand-submissions-sheet-list"
    >
      {submissions.map((submission) => (
        <BrandSubmissionRow
          key={submission.id}
          submission={submission}
          missionId={missionId}
          creatorHandle={collabCreatorHandle}
          canSendKudo={canSendKudo}
          coupDeCoeur={coupDeCoeur}
          agencyDropped={agencyDropped}
        />
      ))}
    </ul>
  )
}

interface RowProps {
  submission: Submission
  missionId: number
  creatorHandle: string | null
  canSendKudo: boolean
  coupDeCoeur: MissionCoupDeCoeur | null
  agencyDropped: boolean
}

function BrandSubmissionRow({
  submission,
  missionId,
  creatorHandle,
  canSendKudo,
  coupDeCoeur,
  agencyDropped,
}: RowProps) {
  const { t } = useTranslation()

  // OKS-964 — three mutually exclusive row states (UX §2.1-2.3):
  //   holdsPick      → §2.2 the chip (this row IS the mission's coup de cœur)
  //   coup_de_coeur_url non-null → §2.1 the button
  //   neither        → §2.3 nothing at all
  //
  // §2.3 is the steady state of a picked mission, NOT an error: nothing red,
  // nothing disabled. The cap line in the header carries the explanation.
  const holdsPick = coupDeCoeur?.submission_id === submission.id

  return (
    <li
      className="flex items-start gap-3 rounded-md border p-3"
      data-testid={`brand-submission-row-${submission.id}`}
    >
      {/* OKS-506 — Surface A (brand review Sheet, /brand/missions/:id).
          Fallback fires on a null cover OR when the snapshot cover (signed
          CDN URL, ~6h TTL) 404s after expiry; both render the muted square
          instead of a broken-image glyph. */}
      <ThumbnailWithFallback
        src={submission.tiktok_video_thumbnail_url}
        alt=""
        loading="lazy"
        className="size-24 flex-shrink-0 rounded object-cover"
        fallback={
          <div
            aria-hidden="true"
            data-testid="submission-thumb-fallback"
            className="bg-muted size-24 flex-shrink-0 rounded"
          />
        }
      />

      <div className="flex flex-1 flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <SubmissionStateChip state={submission.state} />
          <a
            href={submission.tiktok_video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground text-sm hover:underline"
            data-testid={`brand-submission-permalink-${submission.id}`}
          >
            <ExternalLink aria-hidden="true" className="inline size-3.5" />
          </a>
        </div>

        {submission.caption_excerpt && (
          <p className="line-clamp-2 text-sm">{submission.caption_excerpt}</p>
        )}

        {submission.state === "approved" && submission.approved_at && (
          <p
            className="text-muted-foreground text-xs"
            data-testid={`brand-submission-approved-meta-${submission.id}`}
          >
            {t("submissions.brand.audit_approved_by", {
              actor: "—",
              when: submission.approved_at,
            })}
          </p>
        )}

        {submission.state === "flagged" && submission.flagged_at && (
          <p
            className="text-muted-foreground text-xs"
            data-testid={`brand-submission-flagged-meta-${submission.id}`}
          >
            {t("submissions.brand.audit_flagged_by", {
              actor: "—",
              when: submission.flagged_at,
            })}
          </p>
        )}

        {/* OKS-964 UX §2.2 — this row HOLDS the pick. A non-interactive chip,
            never a button: the act is irreversible, so there is nothing left to
            click. Icon PLUS text — the filled heart alone would be a colour-only
            signal (UX §6, the OKS-707 stale-cue rule). */}
        {holdsPick && (
          <p
            className="flex items-center gap-1.5 text-xs font-medium"
            data-testid={`submission-coup-de-coeur-chip-${submission.id}`}
          >
            <Heart aria-hidden="true" className="size-3.5 fill-current" />
            {/* ⚠️ `marked_at`, NOT `approved_at`. UX §2.2's `%{when}` is when the
                BRAND PICKED this post — a different event from its approval,
                minutes to days earlier. The row carries `approved_at` and the
                audit line above legitimately renders it, which is precisely why
                the wrong field reads as correct here: both are plausible
                timestamps on the same row, and a fixture where they coincide
                cannot tell them apart. The mark time lives on the MISSION-level
                prop because the pick is a property of the mission. */}
            {t("submissions.brand.coup_de_coeur.chip", {
              when: coupDeCoeur?.marked_at ?? "",
            })}
          </p>
        )}

        {/* OKS-964 UX §2.1 — available: an approved row in an unpicked mission,
            and only when the server said so. The URL carries all three
            conditions (approved + unpicked + policy), so there is nothing to
            re-derive here — and nothing to get out of step with the endpoint. */}
        {!holdsPick && submission.coup_de_coeur_url && (
          <div className="mt-1">
            <CoupDeCoeurConfirmDialog
              action={submission.coup_de_coeur_url}
              submissionId={submission.id}
              creatorHandle={creatorHandle}
            />
          </div>
        )}

        {submission.state === "pending" && (
          <div
            className="mt-1 flex flex-col gap-2 sm:flex-row"
            data-testid={`brand-submission-actions-${submission.id}`}
          >
            <ApproveSubmissionConfirmDialog
              submission={submission}
              missionId={missionId}
              creatorHandle={creatorHandle}
              canSendKudo={canSendKudo}
            />
            <FlagSubmissionConfirmDialog
              submission={submission}
              missionId={missionId}
            />
          </div>
        )}

        {agencyDropped && submission.state === "pending" && (
          <p
            className="text-xs text-amber-700"
            role="status"
            data-testid={`brand-submission-agency-dropped-${submission.id}`}
          >
            {t("submissions.brand.agency_dropped_tooltip")}
          </p>
        )}
      </div>
    </li>
  )
}
